import { FC, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getGenerativeAiModels } from 'entities/generative-models/api';
import { SandboxParams } from 'entities/generative-models/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Switch } from 'shared/ui/Switch';
import { NativeSelect } from 'shared/ui/NativeSelect';
import { SelectOption } from 'shared/ui/Select/types';
import { useFileEditorStore } from 'store/file-editor';
import Collapse from 'components/collapse';
import { isImpersonationMode } from 'shared/helpers/impersonation';

type AISettingsProps = {
	sandbox: SandboxParams;
	setSandbox: (data: SandboxParams) => void;
};

const AISettings: FC<AISettingsProps> = ({ sandbox, setSandbox }) => {
	const { t } = useTranslation();
	const { fileDetails } = useFileEditorStore();

	const { data: aiModelsResponse } = useQuery({
		queryKey: ['get/generative-models/ai-models'],
		queryFn: getGenerativeAiModels,
		enabled: isImpersonationMode(),
		refetchOnWindowFocus: false,
	});

	const aiModelsOptions: SelectOption[] = useMemo(
		() => [
			{ value: '', label: '-' },
			...(aiModelsResponse?.data ?? []).map((model) => ({ value: model.id, label: model.id })),
		],
		[aiModelsResponse],
	);

	const fileDetailsContent = useMemo(
		() => [
			{
				label: t('shared_texts.table_columns.full_model_name'),
				content: (
					<NativeSelect
						value={sandbox.fullModelName || ''}
						placeholder=""
						options={aiModelsOptions}
						onSelect={(value) => setSandbox({ ...sandbox, fullModelName: value as string })}
						sx={{ width: '100%' }}
					/>
				),
			},
			{
				label: t('shared_texts.table_columns.reasoning_effort'),
				content: (
					<NativeSelect
						value={sandbox.reasoningEffort || ''}
						placeholder=""
						options={reasoningEffortsOptions}
						onSelect={(value) => setSandbox({ ...sandbox, reasoningEffort: value as string })}
						sx={{ width: '100%' }}
					/>
				),
			},
			{
				label: t('shared_texts.table_columns.extract_regions'),
				content: (
					<Switch
						checked={Boolean(sandbox.extractRegions)}
						onChange={(e) => setSandbox({ ...sandbox, extractRegions: e.target.checked })}
					/>
				),
			},
			{
				label: t('shared_texts.table_columns.return_debug_info'),
				content: (
					<Switch
						checked={Boolean(sandbox.returnDebugInfo)}
						onChange={(e) => setSandbox({ ...sandbox, returnDebugInfo: e.target.checked })}
					/>
				),
			},
		],
		[fileDetails, sandbox, setSandbox, aiModelsOptions],
	);

	if (!isImpersonationMode()) return null;

	return (
		<Collapse label={t('files.details.files') as string}>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
				{fileDetailsContent.map((item, index) => (
					<Box
						key={`details-${index}`}
						sx={{
							display: 'flex',
							alignItems: 'center',
						}}
					>
						<Typography
							variant={'body2'}
							color={ColorPalette.baliHai}
							sx={{ display: 'flex', alignItems: 'center', flex: '1 1' }}
						>
							{item.label}
						</Typography>
						<Typography
							variant={'body2'}
							color={ColorPalette.astronaut}
							sx={{ display: 'flex', alignItems: 'center', flex: '1 1' }}
						>
							{item.content}
						</Typography>
					</Box>
				))}
			</Box>
		</Collapse>
	);
};

export default AISettings;

const reasoningEffortsOptions = [
	{
		value: '',
		label: '-',
	},
	{
		value: 'low',
		label: 'Low',
	},
	{
		value: 'medium',
		label: 'Medium',
	},
	{
		value: 'high',
		label: 'High',
	},
];
